(() => {
  const PAGE_FETCH_RETRIES = 2;
  const PAGE_FETCH_CONCURRENCY = 4;
  const PAGE_FETCH_TIMEOUT = 15000;
  const PAGE_FETCH_RETRY_DELAY = 400;
  const normalizeText = (value) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleUpperCase();

  const groupKey = (title) => {
    const first = normalizeText(title).charAt(0);
    if (/[A-Z]/.test(first)) return first;
    if (/[\u0400-\u04ff]/.test(first)) return 'CYR';
    if (/[\u0600-\u06ff]/.test(first)) return 'AR';
    return '#';
  };

  class BrandsDirectory {
    constructor(root) {
      this.root = root;
      this.list = root.querySelector('[data-brands-list]');
      this.search = root.querySelector('[data-brands-search]');
      this.status = root.querySelector('[data-brands-status]');
      this.letterBar = root.querySelector('[data-brands-letters]');
      this.source = root.querySelector('[data-brands-source]');
      this.items = this.readItems(this.source);
      this.complete = Number(this.source?.dataset.sourcePages || 1) <= 1;
      this.loading = !this.complete;
      this.activeLetter = 'all';
      this.expandedGroups = new Set();
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.collator = new Intl.Collator(document.documentElement.lang || 'en', { sensitivity: 'base' });
    }

    readItems(container) {
      if (!container) return [];
      return [...container.querySelectorAll('[data-brands-source-item]')].map((link) => ({
        handle: link.dataset.brandHandle || '',
        title: link.dataset.brandTitle || link.textContent.trim(),
        url: link.getAttribute('href') || '#',
      }));
    }

    init() {
      if (!this.list || !this.search || !this.source) return;
      this.root.classList.add('is-enhanced');
      this.updateCount();
      this.bind();
      this.render();
      this.loadRemainingPages();
    }

    bind() {
      this.search.addEventListener('input', () => {
        this.activeLetter = 'all';
        this.expandedGroups.clear();
        this.render();
      });

      this.letterBar?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-brand-letter]');
        if (!button || button.disabled) return;
        this.activeLetter = button.dataset.brandLetter || 'all';
        this.search.value = '';
        this.expandedGroups.clear();
        this.render();
        this.list.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.list.addEventListener('click', (event) => {
        const expand = event.target.closest('[data-brand-group-expand]');
        if (expand) {
          this.expandedGroups.add(expand.dataset.brandGroupExpand);
          this.render();
          return;
        }

        const reset = event.target.closest('[data-brands-reset]');
        if (reset) {
          this.search.value = '';
          this.activeLetter = 'all';
          this.expandedGroups.clear();
          this.render();
          this.search.focus();
        }
      });

      this.mobile.addEventListener?.('change', () => this.render());
    }

    async loadRemainingPages() {
      const pages = Number(this.source.dataset.sourcePages || 1);
      if (pages <= 1) return;

      this.status.textContent = this.root.dataset.loadingLabel || '';
      const sectionId = this.root.dataset.sectionId;
      const pageParam = this.source.dataset.pageParam || 'page';
      const currentPage = Number(this.source.dataset.sourcePage || 1);
      const pageItems = new Map([[currentPage, this.items]]);
      const queue = Array.from({ length: pages }, (_, index) => index + 1)
        .filter((page) => page !== currentPage);
      const failedPages = [];
      let nextPage = 0;
      const worker = async () => {
        while (nextPage < queue.length) {
          const page = queue[nextPage++];
          try {
            pageItems.set(page, await this.fetchPageWithRetry(page, sectionId, pageParam));
          } catch (error) {
            failedPages.push(page);
            console.warn(`LETOILE brands directory page ${page}:`, error);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(PAGE_FETCH_CONCURRENCY, queue.length) }, worker));

      const merged = [...pageItems.entries()].sort(([a], [b]) => a - b).flatMap(([, items]) => items);
      this.items = [...new Map(merged.map((item) => [item.handle || item.title, item])).values()];
      this.loading = false;
      this.complete = failedPages.length === 0;
      this.status.textContent = this.complete ? '' : this.root.dataset.loadErrorLabel || '';
      this.updateCount();
      this.render();
    }

    updateCount() {
      const count = this.root.querySelector('[data-brands-count]');
      if (count) {
        count.hidden = !this.complete;
        count.textContent = this.complete ? String(this.items.length) : '';
      }
      this.root.dataset.brandsLoadState = this.loading ? 'loading' : this.complete ? 'ready' : 'partial';
      this.root.setAttribute('aria-busy', String(this.loading));
    }

    async waitForRequestSlot() {
      for (;;) {
        const delay = Math.max(this.requestNotBefore || 0, this.nextRequestAt || 0) - Date.now();
        if (delay <= 0) {
          this.nextRequestAt = Date.now() + (this.requestGap || 0);
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, delay));
      }
    }

    async fetchPageWithRetry(page, sectionId, pageParam) {
      let lastError;

      for (let attempt = 0; attempt <= PAGE_FETCH_RETRIES; attempt += 1) {
        try {
          return await this.fetchPage(page, sectionId, pageParam);
        } catch (error) {
          lastError = error;
          const canRetry = error?.retryable !== false && attempt < PAGE_FETCH_RETRIES;
          if (!canRetry) throw error;
          await new Promise((resolve) => window.setTimeout(resolve, Math.max(PAGE_FETCH_RETRY_DELAY * 2 ** attempt, error.retryAfterMs || 0)));
        }
      }

      throw lastError;
    }

    async fetchPage(page, sectionId, pageParam) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set(pageParam, String(page));
      await this.waitForRequestSlot();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), PAGE_FETCH_TIMEOUT);
      let response;
      let html;
      try {
        response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        html = await response.text();
      } finally {
        window.clearTimeout(timeout);
      }
      if (!response.ok) {
        const error = new Error(`Section page ${page} returned ${response.status}`);
        error.retryable = response.status === 429 || response.status >= 500;
        const retryAfter = response.headers.get('Retry-After');
        error.retryAfterMs = retryAfter
          ? Math.max(0, /^\d+(?:\.\d+)?$/.test(retryAfter) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now()) || 0
          : 0;
        if (response.status === 429) {
          this.requestNotBefore = Math.max(this.requestNotBefore || 0, Date.now() + Math.max(1000, error.retryAfterMs));
          this.requestGap = PAGE_FETCH_RETRY_DELAY;
        }
        throw error;
      }
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const source = documentFragment.querySelector(`[data-brands-source][data-source-page="${page}"]`);
      if (!source) {
        const error = new Error(`Section page ${page} did not contain brand data`);
        error.retryable = false;
        throw error;
      }
      return this.readItems(source);
    }

    groupsFor(items) {
      const groups = new Map();
      for (const item of items) {
        const key = groupKey(item.title);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      }
      for (const values of groups.values()) values.sort((left, right) => this.collator.compare(left.title, right.title));
      return groups;
    }

    orderedKeys(groups) {
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
      return [...alphabet, 'AR', 'CYR', '#'].filter((key) => groups.has(key));
    }

    render() {
      const query = normalizeText(this.search.value || '');
      const matching = query
        ? this.items.filter((item) => normalizeText(item.title).includes(query))
        : this.items;
      const groups = this.groupsFor(matching);
      const visibleKeys = this.activeLetter === 'all' ? this.orderedKeys(groups) : [this.activeLetter].filter((key) => groups.has(key));

      this.updateLetterButtons(this.groupsFor(this.items));
      this.list.replaceChildren();

      if (!visibleKeys.length) {
        if (this.complete) this.list.append(this.emptyState());
        return;
      }

      for (const key of visibleKeys) this.list.append(this.groupElement(key, groups.get(key), Boolean(query)));
    }

    updateLetterButtons(allGroups) {
      for (const button of this.letterBar?.querySelectorAll('[data-brand-letter]') || []) {
        const key = button.dataset.brandLetter;
        const active = key === this.activeLetter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.disabled = this.complete && key !== 'all' && !allGroups.has(key);
      }
    }

    groupElement(key, items, searching) {
      const group = document.createElement('section');
      group.className = 'le-brands__group';
      group.dataset.brandGroup = key;

      const heading = document.createElement('h2');
      heading.textContent = key;
      const body = document.createElement('div');
      body.className = 'le-brands__group-body';
      const grid = document.createElement('div');
      grid.className = 'le-brands__grid';

      const limit = this.mobile.matches
        ? Number(this.root.dataset.mobileLimit || 16)
        : Number(this.root.dataset.desktopLimit || 40);
      const expanded = searching || this.activeLetter !== 'all' || this.expandedGroups.has(key);
      const visibleItems = expanded ? items : items.slice(0, limit);

      for (const item of visibleItems) {
        const link = document.createElement('a');
        link.href = item.url;
        link.textContent = item.title;
        link.dataset.brandHandle = item.handle;
        grid.append(link);
      }

      body.append(grid);
      if (!expanded && items.length > limit) {
        const showAll = document.createElement('button');
        showAll.className = 'le-brands__show-all';
        showAll.type = 'button';
        showAll.dataset.brandGroupExpand = key;
        const desktopLabel = this.root.dataset.showAllLabel || 'Show all';
        const mobileLabel = this.root.dataset.mobileShowAllLabel || 'All brands starting with [letter]';
        showAll.textContent = this.mobile.matches
          ? mobileLabel.replace('[letter]', key === '#' ? '0–9' : key)
          : desktopLabel;
        body.append(showAll);
      }

      group.append(heading, body);
      return group;
    }

    emptyState() {
      const wrapper = document.createElement('div');
      wrapper.className = 'le-brands__empty';
      const title = document.createElement('p');
      title.textContent = this.root.dataset.noResultsLabel || 'Sorry, nothing found';
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.dataset.brandsReset = '';
      reset.textContent = this.root.dataset.resetLabel || 'Reset filter';
      wrapper.append(title, reset);
      return wrapper;
    }
  }

  const init = () => document.querySelectorAll('[data-brands-directory]').forEach((root) => new BrandsDirectory(root).init());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
